import {
  consumeImplicitJumpToken,
  socialHeadlessCall,
} from '$api/headless-api';
import { completeAccountFlow } from '$flows/complete-account/complete-account';
import { createTokenExchangeErrorOutcome } from '$flows/helpers/token-exchange-error';
import {
  CloseModalOutcome,
  UserCreatedOutcome,
} from '$pages/variants/complete-account/controller';
import { PageOutcome, largeButtons } from '$pages/variants/large-buttons';
import { InitialState as LargeButtonsState } from '$pages/variants/large-buttons/state';
import { AsyncWrapper } from '$scripts/async-wrapper';
import { AppEvents } from '$scripts/events/events';
import { Provider, PROVIDERS } from '$shared/types/social-provider';
import { getApplicationState } from '$store/application-store';
import { getConfigurationsState } from '$store/configurations-store';
import { AdobeCreateAccountTrait } from '$traits/adobe-create-account';
import { AdobeSignInTrait } from '$traits/adobe-sign-in';
import { defaultHandleAuthFailed } from '$traits/handlers';
import { AuthFailedOutcome, CompleteAccountRequiredOutcome } from '$traits/outcomes';
import { SocialSignInTrait } from '$traits/social-sign-in';
import { envs } from '@sentry/shared';

import { endOfFlow } from '../../helpers/end-of-flow';
import { FlowConfig } from './schema';
import { ENVIRONMENTS } from '@sentry/shared/dist/environments';
import { createInteractionEvent, createStateEvent } from '$scripts/analytics/events';
import { getAnalyticsState } from '$store/analytics-store';
import { getLoggers } from '$shared/loggers';

/**
 *  Example of preload:
 *   const pages = [largeButtons, errorPage].reduce(
        (acc, page) => acc.then(() => page.load()),
        Promise.resolve()
      );
 * 
 */

export async function largeButtonsFlow(
  events: AppEvents,
  config: FlowConfig
): Promise<any> {
  const { fetchAndCacheSocialProviders } = getConfigurationsState();
  const { hasPasskeys } = config.features;
  const { logInfo } = getLoggers();


  logInfo('[large-buttons-flow] started');

  const providers = AsyncWrapper.from(
    fetchAndCacheSocialProviders().then((socialProviders: Provider[]) =>
      socialProviders.concat(hasPasskeys ? [PROVIDERS.passkey] : [])
    )
  );

  const pageState: LargeButtonsState = {
    async: { providers },
    maxSocialProviders: 3,
  };

  logInfo('[large-buttons-flow] navigating to large buttons page');
  let outcome = await largeButtons.create(pageState);

  outcome = AdobeSignInTrait.withPopupOrRedirect.handle(
    events,
    outcome
  ) as PageOutcome;
  outcome = AdobeCreateAccountTrait.withPopupOrRedirect.handle(
    events,
    outcome
  ) as PageOutcome;
  outcome = SocialSignInTrait.withPopupOrRedirect.handle(
    events,
    outcome
  ) as PageOutcome;

  // TODO: Investigate and fix type issue
  if (outcome.of(CompleteAccountRequiredOutcome)) {
    const { socialProvider: provider, client_id } = outcome.getValue();
    const { push } = getAnalyticsState();

    logInfo('[large-buttons-flow] CompleteAccountRequiredOutcome received');

    // SUSI-23016: Line 76 is needed for the Complete Account A/B test
    push(createStateEvent({ stateName: 'Account:IMS-Light:complete-account:OnLoad' }));

    outcome = await completeAccountFlow(
      events,
      {
        consentProfile: config.consentProfile,
        enableCompleteAccount: true,
      },
      outcome.getValue()
    );

    logInfo('completeAccountFlow executed, returning to largeButtonsFlow');

    if (outcome.of(UserCreatedOutcome)) {
      // // Use token to sign in
      const { token } = outcome.getValue();
      logInfo('[large-buttons-flow] UserCreatedOutcome received');

      const searchParams = new URLSearchParams(window.location.search);
      const responseType =
        envs.getAppEnv() === ENVIRONMENTS.local ||
          (envs.getAppEnv() === ENVIRONMENTS.stage &&
            searchParams.get('disable_local_msw') === 'true')
          ? 'locally_mocked_valid_user'
          : 'implicit_jump';

      push(createInteractionEvent({ eventName: 'AccountCreated' }));

      try {
        logInfo('[large-buttons-flow] Social headless call');
        const headless = await socialHeadlessCall(
          token,
          provider,
          client_id,
          responseType
        );
        logInfo('[large-buttons-flow] Social headless call completed');

        // // Do token exchange
        logInfo('[large-buttons-flow] Implicit jump token call');
        const res = await consumeImplicitJumpToken(headless.token, client_id);
        logInfo('[large-buttons-flow] Implicit jump token consumed');

        push(createInteractionEvent({ eventName: 'ProcessComplete' }));

        // // Send event to client
        await logInfo('[large-buttons-flow] Sending token to client');
        events.sendToken(res.access_token);

        // // End of flow
        await logInfo('[large-buttons-flow] Sending CloseModal event');
        outcome.setHandled();
        events.closeModal();

        return endOfFlow(events, outcome);
      } catch (e: unknown) {
        await logInfo('[large-buttons-flow] Error during token exchange, handling error');
        const { queryState } = await getApplicationState();
        const errorOutcome = await createTokenExchangeErrorOutcome(
          client_id,
          queryState,
          token,
          provider
        );
        return defaultHandleAuthFailed(events, errorOutcome);
      }
    }

    if (outcome.of(CloseModalOutcome)) {
      await logInfo('[large-buttons-flow] CloseModalOutcome received, restarting largeButtonsFlow');
      return await largeButtonsFlow(events, config);
    }
  }
  logInfo('[large-buttons-flow] ended with unhandled outcome');
  return endOfFlow(events, outcome);
}
